import { useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Select,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { bathOptionsArray, bedOptionsArray } from '@zg-rentals/zillow-rental-manager/constants';

import { useCreateFeedFloorPlan } from '../../../hooks/useCreateFeedFloorPlan';
import RMWInput from '../../RMWInput';

import type { DatarouterError } from '@zg-rentals/web-base';

type Props = {
  listingAlias: string;
  onClose: () => void;
};

const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Feed floor plans are created with all fields at once (the backend requires beds up front), unlike
 * the manual flow that creates an empty floor plan and edits it field-by-field.
 */
const FeedFloorPlanCreateModal = ({ listingAlias, onClose }: Props): JSX.Element => {
  const { enqueueToast } = useToast();
  const { createFeedFloorPlan, isPending } = useCreateFeedFloorPlan(listingAlias);

  const [name, setName] = useState('');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [squareFeet, setSquareFeet] = useState('');
  const [lowPrice, setLowPrice] = useState('');
  const [highPrice, setHighPrice] = useState('');
  const [description, setDescription] = useState('');
  const [bedsError, setBedsError] = useState('');

  const handleCreate = async () => {
    if (beds === '') {
      setBedsError('Select the number of beds');
      return;
    }

    const bathsValue = baths === '' ? undefined : Number(baths);

    try {
      await createFeedFloorPlan({
        numBedrooms: Number(beds),
        name: name.trim() || undefined,
        numFullBaths: bathsValue === undefined ? undefined : Math.floor(bathsValue),
        numHalfBaths: bathsValue === undefined ? undefined : bathsValue % 1 === 0 ? 0 : 1,
        squareFeet: parseOptionalNumber(squareFeet),
        lowPrice: parseOptionalNumber(lowPrice),
        highPrice: parseOptionalNumber(highPrice),
        description: description.trim() || undefined,
      });
      onClose();
    } catch (error) {
      enqueueToast(
        <Toast tone="critical">
          {(error as DatarouterError)?.message || 'Floor plan couldn’t be created. Try again.'}
        </Toast>,
      );
    }
  };

  return (
    <Modal
      header={<Heading level={2}>Add floor plan</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <RMWInput
              flex="1"
              fieldName="name"
              label="Floor plan name"
              showOptional={true}
              onChange={(event) => setName(event.target.value)}
            />

            <Box css={{ flex: '1' }}>
              <FormField
                required={true}
                label={<Label>Beds</Label>}
                error={Boolean(bedsError)}
                control={
                  <Select
                    name="beds"
                    value={beds}
                    onChange={(event) => {
                      setBeds(event.target.value);
                      setBedsError('');
                    }}
                  >
                    <option disabled={true} value="">
                      Select
                    </option>
                    {bedOptionsArray.map(({ label, value }) => (
                      <option key={label} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                }
                description={Boolean(bedsError) && <FormHelp>{bedsError}</FormHelp>}
              />
            </Box>
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <Box css={{ flex: '1' }}>
              <FormField
                label={<Label>Baths</Label>}
                control={
                  <Select name="baths" value={baths} onChange={(event) => setBaths(event.target.value)}>
                    <option disabled={true} value="">
                      Select
                    </option>
                    {bathOptionsArray.map(({ label, value }) => (
                      <option key={label} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                }
              />
            </Box>

            <RMWInput
              flex="1"
              fieldName="squareFeet"
              label="Square footage"
              showOptional={true}
              onChange={(event) => setSquareFeet(event.target.value)}
            />
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <RMWInput
              flex="1"
              fieldName="lowPrice"
              label="Min price"
              inputType="adorned"
              leftAdornment="$"
              rightAdornment="/mo"
              showOptional={true}
              onChange={(event) => setLowPrice(event.target.value)}
            />

            <RMWInput
              flex="1"
              fieldName="highPrice"
              label="Max price"
              inputType="adorned"
              leftAdornment="$"
              rightAdornment="/mo"
              showOptional={true}
              onChange={(event) => setHighPrice(event.target.value)}
            />
          </Box>

          <RMWInput
            fieldName="description"
            label="Description"
            inputType="textarea"
            showOptional={true}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Add floor plan actions">
          <Modal.Close>
            <TextButton>Cancel</TextButton>
          </Modal.Close>

          <Button emphasis="filled" onClick={handleCreate} loading={isPending} disabled={beds === ''}>
            Add floor plan
          </Button>
        </ButtonGroup>
      }
      useDismissProps={{ outsidePress: false }}
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', lg: 'md' }}
    />
  );
};

export default FeedFloorPlanCreateModal;
